import { Alert, Button, Card, Flex, List, Tag, Typography } from "antd"
import type { TFunction } from "i18next"
import React from "react"
import { useTranslation } from "react-i18next"
import { useNavigate } from "react-router-dom"

import { DownloadCopy } from "../../../components/Esign/signing/DownloadCopy.tsx"
import { TOUCH } from "../../../components/Esign/signing/touch.ts"
import { certificateName } from "../../../components/Esign/signing/types.ts"
import { RoutesPaths } from "../../../constants/routes.ts"
import {
  saveEnvelopeCertificate,
  saveEnvelopeDocument
} from "../api/esign.services.ts"
import { isCountersigner } from "../helpers/countersigner.ts"
import { ENVELOPE_STATUS_TAG_COLORS } from "../helpers/envelope-status-tag-colors.ts"
import { joinNames } from "../helpers/join-names.ts"
import {
  type Headline,
  headlineOf
} from "../helpers/signature-request-headline.ts"
import { useCanCountersign } from "../hooks/use-countersign.ts"
import { useEnvelopeLifecycle } from "../hooks/use-envelope-lifecycle.tsx"
import { useLeadExternalSignatures } from "../hooks/use-lead-external-signatures.ts"
import { useLeadSignatureRequests } from "../hooks/use-lead-signature-requests.ts"
import type {
  EnvelopeRecipient,
  SigningEnvelope
} from "../types/esign.types.ts"
import { ExternalSignatureCard } from "./ExternalSignatureCard.tsx"

const { Text } = Typography

const pdfName = (name: string) => (/\.pdf$/i.test(name) ? name : `${name}.pdf`)

interface SignatureRequestsPanelProps {
  leadId: string | undefined
  /** Bumped by the caller after it files a document signed outside EZBuild. */
  reloadKey?: number
}

export const SignatureRequestsPanel = ({
  leadId,
  reloadKey = 0
}: SignatureRequestsPanelProps) => {
  const { t } = useTranslation()
  const requests = useLeadSignatureRequests(leadId)
  const external = useLeadExternalSignatures(leadId, reloadKey)
  const lifecycle = useEnvelopeLifecycle(requests.retry)
  if (requests.status === "failed") {
    return (
      <Alert
        type={"error"}
        showIcon
        message={t("esign.panel.loadFailedTitle")}
        description={t("esign.panel.loadFailedBody")}
        action={
          <Button size={"small"} onClick={requests.retry}>
            {t("esign.common.retry")}
          </Button>
        }
      />
    )
  }
  const envelopes = requests.status === "ready" ? requests.envelopes : []
  if (envelopes.length === 0 && external.length === 0) {
    return null
  }
  return (
    <Flex vertical gap={"small"}>
      {external.map((signature) => (
        <ExternalSignatureCard key={signature.id} signature={signature} />
      ))}
      {envelopes.map((envelope) => (
        <SignatureRequest
          key={envelope.id}
          envelope={envelope}
          lifecycle={lifecycle}
        />
      ))}
      {lifecycle.dialog}
    </Flex>
  )
}

type Lifecycle = ReturnType<typeof useEnvelopeLifecycle>

function SignatureRequest({
  envelope,
  lifecycle
}: {
  envelope: SigningEnvelope
  lifecycle: Lifecycle
}) {
  const { t, i18n } = useTranslation()
  const navigate = useNavigate()
  const canCountersign = useCanCountersign()
  const headline = headlineOf(envelope)
  const headlineText = useHeadlineText(headline)
  const name = envelope.subject?.name ?? t("esign.panel.untitled")
  const fileName = envelope.subject?.name ?? "document"
  const to = RoutesPaths.esign.sign(envelope.id)
  const recipients = [...envelope.recipients].sort(
    (a, b) => a.signing_order - b.signing_order
  )

  return (
    <Card size={"small"} role={"region"} aria-label={name}>
      <Flex vertical gap={"small"}>
        <Flex justify={"space-between"} align={"center"} wrap gap={"small"}>
          <Flex vertical gap={0}>
            <Text strong>{name}</Text>
            {headlineText && <Text type={"secondary"}>{headlineText}</Text>}
            {envelope.expires_at && lifecycle.mayResend(envelope.status) && (
              <Text type={"secondary"} className={"text-xs"}>
                {t("esign.lifecycle.expiresOn", {
                  date: new Intl.DateTimeFormat(i18n.language, {
                    month: "short",
                    day: "numeric",
                    year: "numeric"
                  }).format(new Date(envelope.expires_at))
                })}
              </Text>
            )}
          </Flex>
          <Flex align={"center"} gap={"small"}>
            <Tag color={ENVELOPE_STATUS_TAG_COLORS[envelope.status]}>
              {t(`esign.statuses.${envelope.status}`)}
            </Tag>
            {lifecycle.mayResend(envelope.status) && (
              <Button
                size={"small"}
                loading={lifecycle.resending === envelope.id}
                aria-label={t("esign.lifecycle.resendFor", { document: name })}
                onClick={() => lifecycle.resend(envelope.id)}
              >
                {t("esign.lifecycle.resend")}
              </Button>
            )}
            {lifecycle.mayVoid(envelope.status) && (
              <Button
                size={"small"}
                danger
                aria-label={t("esign.lifecycle.voidFor", { document: name })}
                onClick={() => lifecycle.startVoid(envelope.id, String(name))}
              >
                {t("esign.lifecycle.void")}
              </Button>
            )}
            {lifecycle.mayDelete(envelope.status) && (
              <Button
                size={"small"}
                danger
                aria-label={t("esign.lifecycle.deleteFor", { document: name })}
                onClick={() => lifecycle.startDelete(envelope.id, String(name))}
              >
                {t("esign.lifecycle.delete")}
              </Button>
            )}
            {headline?.companyWaiting && canCountersign && (
              <Button
                type={"primary"}
                href={to}
                aria-label={t("esign.countersign.openDocument", {
                  document: name
                })}
                style={TOUCH}
                onClick={(event) => {
                  // Modified clicks belong to the browser (new tab, new window).
                  if (
                    event.defaultPrevented ||
                    event.button !== 0 ||
                    event.metaKey ||
                    event.ctrlKey ||
                    event.shiftKey ||
                    event.altKey
                  )
                    return
                  event.preventDefault()
                  navigate(to)
                }}
              >
                {t("esign.countersign.open")}
              </Button>
            )}
          </Flex>
        </Flex>
        {envelope.seal && (
          <Flex gap={"small"} wrap>
            <DownloadCopy
              compact
              download={() =>
                saveEnvelopeDocument(envelope.id, pdfName(fileName))
              }
              label={t("esign.panel.downloadSigned")}
            />
            <DownloadCopy
              compact
              download={() =>
                saveEnvelopeCertificate(envelope.id, certificateName(fileName))
              }
              label={t("signing.certificate.download")}
              failedText={t("signing.certificate.failed")}
            />
          </Flex>
        )}
        <List
          size={"small"}
          dataSource={recipients}
          rowKey={(recipient) => recipient.id}
          renderItem={(recipient) => (
            <RecipientLine recipient={recipient} envelope={envelope} />
          )}
        />
      </Flex>
    </Card>
  )
}

const awaitsItsTurn = (
  recipient: EnvelopeRecipient,
  envelope: SigningEnvelope
) =>
  isCountersigner(recipient) &&
  envelope.turn != null &&
  recipient.signing_order > envelope.turn

const signedText = (
  recipient: EnvelopeRecipient,
  t: TFunction,
  language: string
): string => {
  if (isCountersigner(recipient) && recipient.signed_by) {
    return t("esign.panel.signedBy", { name: recipient.signed_by })
  }
  if (!recipient.signed) return t("esign.recipientStatuses.signed")
  const date = new Intl.DateTimeFormat(language, {
    month: "short",
    day: "numeric"
  }).format(new Date(recipient.signed))
  return t("esign.panel.signedOn", { date })
}

const recipientStatusText = (
  recipient: EnvelopeRecipient,
  envelope: SigningEnvelope,
  t: TFunction,
  language: string
): string => {
  if (recipient.status === "signed") return signedText(recipient, t, language)
  if (awaitsItsTurn(recipient, envelope)) return t("esign.panel.notItsTurn")
  return t(`esign.recipientStatuses.${recipient.status}`)
}

function RecipientLine({
  recipient,
  envelope
}: {
  recipient: EnvelopeRecipient
  envelope: SigningEnvelope
}) {
  const { t, i18n } = useTranslation()
  return (
    <List.Item>
      <Flex justify={"space-between"} wrap gap={"small"} className={"w-full"}>
        <Flex gap={"small"} align={"center"} wrap>
          <Text type={"secondary"}>{recipient.signing_order}</Text>
          <Text>{recipient.name}</Text>
          <Tag>{t(`esign.roles.${recipient.role}`)}</Tag>
        </Flex>
        <Text>
          {recipientStatusText(recipient, envelope, t, i18n.language)}
        </Text>
      </Flex>
    </List.Item>
  )
}

function useHeadlineText(headline: Headline | null): string | null {
  const { t, i18n } = useTranslation()
  if (!headline) return null
  const list = (names: string[]) => joinNames(names, i18n.language)
  switch (headline.key) {
    case "declined":
      return t("esign.panel.declined", {
        name: headline.name,
        reason: headline.reason
      })
    case "everyoneSigned":
      return t("esign.panel.everyoneSigned")
    case "completed":
      return t("esign.panel.completedOn", {
        date: new Intl.DateTimeFormat(i18n.language, {
          month: "short",
          day: "numeric",
          year: "numeric"
        }).format(new Date(headline.at))
      })
    case "waitingOnCompany":
      return t("esign.panel.waitingOnCompany")
    case "waitingOnMore":
      return t("esign.panel.waitingOnMore", {
        names: list(headline.names),
        count: headline.count
      })
    case "waitingOnMoreAndCompany":
      return t("esign.panel.waitingOnMoreAndCompany", {
        names: list(headline.names),
        count: headline.count
      })
    case "waitingOn":
      return t("esign.panel.waitingOn", {
        names: list(
          headline.companyWaiting
            ? [...headline.names, t("esign.panel.yourCompany")]
            : headline.names
        )
      })
  }
}
